// src/api/entrenadores.js
import { apiFetch, getCurrentApiUrl, getToken, getCurrentTenant } from './index.js';
import { toTitleCase } from '@/utils/stringUtils.js';

const getApiBaseUrl = () => `${getCurrentApiUrl()}/api`;
const getApiStaticUrl = () => getCurrentApiUrl();

// ============================================================
// HELPER: Obtener tenant ID correcto (con fallbacks)
// ============================================================
const resolverTenantId = () => {
  try {
    let tenant = getCurrentTenant?.();
    if (tenant && tenant !== 'default' && tenant.trim() !== '') return tenant;

    tenant = localStorage.getItem('selectedTenantId');
    if (tenant && tenant !== 'default' && tenant.trim() !== '') return tenant;

    tenant = localStorage.getItem('currentTenant');
    if (tenant && tenant !== 'default' && tenant.trim() !== '') return tenant;

    console.warn('⚠️ No se encontró tenant, usando default');
    return 'default';
  } catch (e) {
    console.error('Error resolviendo tenant:', e);
    return 'default';
  }
};

// ============================================================
// CRUD PRINCIPAL
// ============================================================

export const obtenerEntrenadores = async (estado = 'all') => {
  try {
    console.log('🏋️ Obteniendo entrenadores...', { estado });

    let endpoint = '/Entrenador';
    if (estado === 'activos') endpoint = '/Entrenador/activos';

    const response = await apiFetch(endpoint);

    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Entrenadores obtenidos exitosamente'
    };
  } catch (error) {
    console.error('❌ Error obteniendo entrenadores:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const obtenerEntrenadorPorId = async (id) => {
  try {
    if (!id) throw new Error('ID de entrenador es requerido');
    const response = await apiFetch(`/Entrenador/${id}`);
    return { success: true, data: response, message: 'Entrenador obtenido' };
  } catch (error) {
    console.error(`❌ Error obteniendo entrenador ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const obtenerEntrenadorPorUsuario = async (idUsuario) => {
  try {
    if (!idUsuario) throw new Error('ID de usuario es requerido');
    const response = await apiFetch(`/Entrenador/usuario/${idUsuario}`);
    return { success: true, data: response, message: 'Entrenador obtenido por usuario' };
  } catch (error) {
    console.error(`❌ Error obteniendo entrenador del usuario ${idUsuario}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const obtenerEntrenadoresPorSede = async (idSede) => {
  try {
    if (!idSede) throw new Error('ID de sede es requerido');
    const response = await apiFetch(`/Entrenador/sede/${idSede}`);
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0
    };
  } catch (error) {
    console.error(`❌ Error obteniendo entrenadores de sede ${idSede}:`, error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const buscarEntrenadores = async (searchDTO) => {
  try {
    const response = await apiFetch('/Entrenador/search', {
      method: 'POST',
      body: JSON.stringify(searchDTO)
    });
    const data = response.data || response;
    return {
      success: true,
      data: data,
      count: Array.isArray(data) ? data.length : 0
    };
  } catch (error) {
    console.error('❌ Error buscando entrenadores:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const verificarEntrenadorExiste = async (id) => {
  try {
    if (!id) throw new Error('ID es requerido');
    const response = await apiFetch(`/Entrenador/${id}/exists`);
    const existe = typeof response === 'boolean' ? response : response.existe || false;
    return { success: true, existe };
  } catch (error) {
    return { success: false, error: error.message, existe: false };
  }
};

// ============================================================
// CREAR ENTRENADOR (con X-Tenant-ID)
// ============================================================

export const crearEntrenador = async (data) => {
  try {
    if (!data.idUsuario) throw new Error('El idUsuario es requerido');
    if (!data.nombre) throw new Error('El nombre es requerido');
    if (!data.apellido) throw new Error('El apellido es requerido');

    const formData = new FormData();
    formData.append('idUsuario', data.idUsuario);
    if (data.idSede) formData.append('idSede', data.idSede);
    formData.append('nombre', toTitleCase(data.nombre));
    formData.append('apellido', toTitleCase(data.apellido));
    if (data.cedula) formData.append('cedula', data.cedula);
    if (data.telefono) formData.append('telefono', data.telefono);
    if (data.email) formData.append('email', data.email);
    if (data.biografia) formData.append('biografia', data.biografia);
    if (data.frase_motivacional) formData.append('frase_motivacional', data.frase_motivacional);
    if (data.anios_experiencia != null) formData.append('anios_experiencia', data.anios_experiencia);
    if (data.whatsapp) formData.append('whatsapp', data.whatsapp);
    if (data.instagram) formData.append('instagram', data.instagram);
    if (data.tiktok) formData.append('tiktok', data.tiktok);
    if (data.facebook) formData.append('facebook', data.facebook);
    if (data.especialidades) formData.append('especialidades', data.especialidades);
    if (data.tarifa_sesion_usd != null) formData.append('tarifa_sesion_usd', data.tarifa_sesion_usd);
    if (data.tarifa_sesion_bs != null) formData.append('tarifa_sesion_bs', data.tarifa_sesion_bs);
    if (data.tarifa_mensual_usd != null) formData.append('tarifa_mensual_usd', data.tarifa_mensual_usd);
    if (data.tarifa_mensual_bs != null) formData.append('tarifa_mensual_bs', data.tarifa_mensual_bs);
    formData.append('mostrar_en_web', data.mostrar_en_web !== false);
    if (data.estado) formData.append('estado', data.estado);
    if (data.fecha_contratacion) formData.append('fecha_contratacion', data.fecha_contratacion);
    if (data.comision_porcentaje != null) formData.append('comision_porcentaje', data.comision_porcentaje);
    if (data.foto) formData.append('foto', data.foto);

    const token = getToken();
    const tenantId = resolverTenantId();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (tenantId) headers['X-Tenant-ID'] = tenantId;

    console.log('📤 POST /api/Entrenador');
    console.log('🏢 Tenant:', tenantId);

    const response = await fetch(`${getApiBaseUrl()}/Entrenador`, {
      method: 'POST',
      body: formData,
      headers
    });

    console.log('📥 Status:', response.status);

    if (!response.ok) {
      let errorMsg = `Error ${response.status}`;
      try {
        const err = await response.json();
        errorMsg = err.mensaje || err.message || errorMsg;
      } catch {}
      throw new Error(errorMsg);
    }

    const result = await response.json();
    return {
      success: true,
      data: result.data || result,
      message: result.mensaje || 'Entrenador creado exitosamente'
    };
  } catch (error) {
    console.error('❌ Error creando entrenador:', error);
    return { success: false, error: error.message, data: null };
  }
};

// ============================================================
// ACTUALIZAR ENTRENADOR (con X-Tenant-ID)
// ============================================================

export const actualizarEntrenador = async (id, data) => {
  try {
    if (!id) throw new Error('ID de entrenador es requerido');

    const formData = new FormData();
    if (data.idSede != null) formData.append('idSede', data.idSede);
    if (data.nombre) formData.append('nombre', toTitleCase(data.nombre));
    if (data.apellido) formData.append('apellido', toTitleCase(data.apellido));
    if (data.cedula != null) formData.append('cedula', data.cedula || '');
    if (data.telefono != null) formData.append('telefono', data.telefono || '');
    if (data.email != null) formData.append('email', data.email || '');
    if (data.biografia != null) formData.append('biografia', data.biografia || '');
    if (data.frase_motivacional != null) formData.append('frase_motivacional', data.frase_motivacional || '');
    if (data.anios_experiencia != null) formData.append('anios_experiencia', data.anios_experiencia);
    if (data.whatsapp != null) formData.append('whatsapp', data.whatsapp || '');
    if (data.instagram != null) formData.append('instagram', data.instagram || '');
    if (data.tiktok != null) formData.append('tiktok', data.tiktok || '');
    if (data.facebook != null) formData.append('facebook', data.facebook || '');
    if (data.especialidades != null) formData.append('especialidades', data.especialidades || '');
    if (data.tarifa_sesion_usd != null) formData.append('tarifa_sesion_usd', data.tarifa_sesion_usd);
    if (data.tarifa_sesion_bs != null) formData.append('tarifa_sesion_bs', data.tarifa_sesion_bs);
    if (data.tarifa_mensual_usd != null) formData.append('tarifa_mensual_usd', data.tarifa_mensual_usd);
    if (data.tarifa_mensual_bs != null) formData.append('tarifa_mensual_bs', data.tarifa_mensual_bs);
    formData.append('mostrar_en_web', data.mostrar_en_web !== false);
    if (data.estado) formData.append('estado', data.estado);
    if (data.fecha_contratacion) formData.append('fecha_contratacion', data.fecha_contratacion);
    if (data.comision_porcentaje != null) formData.append('comision_porcentaje', data.comision_porcentaje);
    if (data.foto) formData.append('foto', data.foto);

    const token = getToken();
    const tenantId = resolverTenantId();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (tenantId) headers['X-Tenant-ID'] = tenantId;

    console.log('📤 PUT /api/Entrenador/' + id);
    console.log('🏢 Tenant:', tenantId);

    const response = await fetch(`${getApiBaseUrl()}/Entrenador/${id}`, {
      method: 'PUT',
      body: formData,
      headers
    });

    if (!response.ok) {
      let errorMsg = `Error ${response.status}`;
      try {
        const err = await response.json();
        errorMsg = err.mensaje || err.message || errorMsg;
      } catch {}
      throw new Error(errorMsg);
    }

    const result = await response.json();
    return {
      success: true,
      data: result.data || result,
      message: result.mensaje || 'Entrenador actualizado exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error actualizando entrenador ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

// ============================================================
// ELIMINAR ENTRENADOR
// ============================================================

export const eliminarEntrenador = async (id) => {
  try {
    if (!id) throw new Error('ID es requerido');
    const response = await apiFetch(`/Entrenador/${id}`, { method: 'DELETE' });
    return {
      success: true,
      data: response,
      message: 'Entrenador desactivado exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error eliminando entrenador ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

// ============================================================
// SUBIR FOTO (con X-Tenant-ID)
// ============================================================

export const subirFotoEntrenador = async (archivo, idEntrenador) => {
  try {
    if (!archivo) throw new Error('Archivo es requerido');
    if (!idEntrenador) throw new Error('ID de entrenador es requerido');

    const formData = new FormData();
    formData.append('File', archivo);

    const token = getToken();
    const tenantId = resolverTenantId();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (tenantId) headers['X-Tenant-ID'] = tenantId;

    const response = await fetch(`${getApiBaseUrl()}/Entrenador/${idEntrenador}/foto`, {
      method: 'POST',
      body: formData,
      headers
    });

    if (!response.ok) {
      let errorMsg = `Error ${response.status}`;
      try {
        const err = await response.json();
        errorMsg = err.mensaje || err.message || errorMsg;
      } catch {}
      throw new Error(errorMsg);
    }

    const data = await response.json();
    return {
      success: true,
      data: data,
      ruta: data.ruta,
      message: data.mensaje || 'Foto subida exitosamente'
    };
  } catch (error) {
    console.error('❌ Error subiendo foto de entrenador:', error);
    return { success: false, error: error.message, data: null, ruta: null };
  }
};

// ============================================================
// WEB PÚBLICA
// ============================================================

export const obtenerEntrenadoresPublicos = async () => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/Entrenador/web`);
    if (!response.ok) throw new Error(`Error ${response.status}`);
    const data = await response.json();
    return {
      success: true,
      data: data,
      count: Array.isArray(data) ? data.length : 0
    };
  } catch (error) {
    console.error('❌ Error obteniendo entrenadores públicos:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

// ============================================================
// ⭐ URL DE IMÁGENES — VERSIÓN BLINDADA
// Maneja: URL absoluta, /imagenes/entrenador/..., /uploads/..., /ruta, solo nombre
// ============================================================

export const obtenerUrlFotoEntrenador = (nombreArchivoORuta) => {
  if (!nombreArchivoORuta || typeof nombreArchivoORuta !== 'string') return null;
  const clean = nombreArchivoORuta.trim();
  if (!clean) return null;

  const base = getApiStaticUrl().replace(/\/$/, '');

  // 1. URL absoluta completa
  if (clean.startsWith('http://') || clean.startsWith('https://')) {
    return clean;
  }

  // 2. Ya viene con /imagenes/entrenador/ → limpiar dobles slashes
  if (clean.includes('/imagenes/entrenador/')) {
    const rutaLimpia = clean.replace(/\/+/g, '/');
    return rutaLimpia.startsWith('/') ? `${base}${rutaLimpia}` : `${base}/${rutaLimpia}`;
  }

  // 3. Ruta vieja /uploads/... → extraer solo el nombre y apuntar a carpeta nueva
  if (clean.includes('/uploads/')) {
    const partes = clean.split('/');
    const nombreArchivo = partes[partes.length - 1];
    return `${base}/imagenes/entrenador/${nombreArchivo}`;
  }

  // 4. Ruta absoluta directa (empieza con /)
  if (clean.startsWith('/')) {
    return `${base}${clean}`;
  }

  // 5. Solo nombre de archivo → carpeta estándar
  return `${base}/imagenes/entrenador/${clean}`;
};

// ============================================================
// HELPERS
// ============================================================

export const obtenerNombreCompletoEntrenador = (e) => {
  if (!e) return '';
  const nombre = toTitleCase(e.nombre || '');
  const apellido = toTitleCase(e.apellido || '');
  return `${nombre} ${apellido}`.trim();
};

export const obtenerInicialesEntrenador = (e) => {
  const nombre = obtenerNombreCompletoEntrenador(e);
  if (!nombre) return '??';
  const partes = nombre.split(' ');
  if (partes.length === 1) return partes[0].charAt(0).toUpperCase();
  return (partes[0].charAt(0) + partes[partes.length - 1].charAt(0)).toUpperCase();
};

export const obtenerColorAvatarEntrenador = (texto) => {
  if (!texto) return '#4F46E5';
  const colores = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];
  let hash = 0;
  for (let i = 0; i < texto.length; i++) {
    hash = texto.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colores[Math.abs(hash) % colores.length];
};

export const formatearEstadoEntrenador = (estado) => {
  if (!estado) return 'Sin estado';
  return estado.charAt(0).toUpperCase() + estado.slice(1).toLowerCase();
};

export const construirLinkWhatsapp = (numero) => {
  if (!numero) return null;
  const limpio = numero.replace(/\D/g, '');
  return `https://wa.me/${limpio}`;
};

export const construirLinkInstagram = (usuario) => {
  if (!usuario) return null;
  const limpio = usuario.replace('@', '').trim();
  return `https://instagram.com/${limpio}`;
};

export const construirLinkTikTok = (usuario) => {
  if (!usuario) return null;
  const limpio = usuario.replace('@', '').trim();
  return `https://tiktok.com/@${limpio}`;
};

export const construirLinkFacebook = (usuario) => {
  if (!usuario) return null;
  const limpio = usuario.trim();
  if (limpio.startsWith('http')) return limpio;
  return `https://facebook.com/${limpio}`;
};

export default {
  obtenerEntrenadores,
  obtenerEntrenadorPorId,
  obtenerEntrenadorPorUsuario,
  obtenerEntrenadoresPorSede,
  buscarEntrenadores,
  verificarEntrenadorExiste,
  crearEntrenador,
  actualizarEntrenador,
  eliminarEntrenador,
  subirFotoEntrenador,
  obtenerEntrenadoresPublicos,
  obtenerUrlFotoEntrenador,
  obtenerNombreCompletoEntrenador,
  obtenerInicialesEntrenador,
  obtenerColorAvatarEntrenador,
  formatearEstadoEntrenador,
  construirLinkWhatsapp,
  construirLinkInstagram,
  construirLinkTikTok,
  construirLinkFacebook
};